<template>
  <div class="accordion" id="settings-accordion">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <a
        href="#settings"
        class="settings-option"
        :class="{ active: openTab === 'address' }"
        @click.prevent="openTab = 'address'"
      >
        <i class="far fa-building"></i>
        <span class="title">{{ __('Information') }}</span>
      </a>
      <a
        href="#settings"
        class="settings-option"
        :class="{ active: openTab === 'branding' }"
        @click.prevent="openTab = 'branding'"
      >
        <i class="fas fa-puzzle-piece"></i>
        <span class="title">{{ __('Branding') }}</span>
      </a>
      <a
        href="#settings"
        class="settings-option"
        :class="{ active: openTab === 'reporting' }"
        @click.prevent="openTab = 'reporting'"
      >
        <i class="fas fa-bullhorn"></i>
        <span class="title">{{ __('Reporting') }}</span>
      </a>
      <a
        href="#settings"
        class="settings-option"
        :class="{ active: openTab === 'pms' }"
        @click.prevent="openTab = 'pms'"
      >
        <i class="fas fa-upload"></i>
        <span class="title">{{ __('PMS') }}</span>
      </a>
    </div>
    <div class="card accordion-card" v-if="openTab == 'address'">
      <div class="card-body">
        <form id="general-settings">
          <p class="readable-text">
            This
            <a
              target="_blank"
              href="https://www.practiceweb.co.uk/knowledge/displaying-company-registration-information/"
              >information</a
            >
            is required, so that guests know where the survey emails are coming from. These details will be included in
            survey emails sent to guests or in the survey page. Including this information is
            <strong>important</strong>, because it increases deliverability.
          </p>
          <div class="row">
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('Country') }}*</label>
              <input
                type="text"
                class="form-control"
                v-model="generalInfo.country"
                placeholder="Enter the country of your hotel"
                aria-label="Enter the country of your hotel"
              />
            </div>
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('City') }}*</label>
              <input
                type="text"
                class="form-control"
                v-model="generalInfo.city"
                placeholder="Enter city"
                aria-label="Enter city"
              />
            </div>
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('Full Address') }}*</label>
              <input
                type="text"
                class="form-control"
                v-model="generalInfo.address"
                placeholder="Enter your hotel's address"
                aria-label="Enter your hotel's address"
              />
            </div>
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('Phone') }}*</label>
              <input
                type="text"
                class="form-control"
                v-model="generalInfo.phone"
                placeholder="Your hotel's main phone"
                aria-label="Your hotel's main phone"
              />
            </div>
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('Email Address') }}*</label>
              <input
                type="text"
                class="form-control"
                v-model="generalInfo.email"
                placeholder="Enter an email where guests can contact you"
                aria-label="Enter an email where guests can contact you"
              />
            </div>
            <div class="form-group mb-3 col-md-6">
              <label>{{ __('Strict Consent') }}*</label>
              <div class="custom-control custom-switch">
                <input
                  id="strict_consent"
                  type="checkbox"
                  class="custom-control-input"
                  v-model="generalInfo.strictConsent"
                />  
                <label class="custom-control-label" for="strict_consent"></label>
              </div>
            </div>
            <div class="col-md-12">
              <button
                class="btn btn-primary pull-right cursor-pointer"
                v-bind:disabled="!generalSettingsFilled"
                @click.prevent="updateSettings(generalInfo)"
              >
                {{ __('Save Hotel Information') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="card" v-if="openTab === 'branding'">
      <div class="card-body">
        <form id="image-settings">
          <p class="readable-text">
            {{
              __(
                "Make your survey recognizable! Include your hotel's or hotel group's Logo and a stunning image so that guests can know that it's from you.",
              )
            }}
          </p>
          <div class="form-group text-center">
            <div class="row">
              <div class="col-md-6">
                <label for="brandImage">{{ __('Brand Logo') }}</label>
                <small class="form-text" v-bind:class="{ 'text-danger': teamLogos.length == teamSurveyImagesLimit }"
                  >{{ teamLogos.length }} out of {{ teamSurveyImagesLimit }} uploaded</small
                >
                <small id="brandImageHelp" class="form-text text-muted">{{
                  __('This is the image that will be on the top of your surveys')
                }}</small>
                <div class="row mt-3">
                  <div class="col-md-12" v-if="uploadLogoAllowed">
                    <vue-dropzone
                      v-on:vdropzone-complete="logoUploaded"
                      v-on:vdropzone-success="logoUploadedSuccess"
                      v-on:vdropzone-error="uploadError"
                      v-if="mounted"
                      id="dropzone-logo"
                      :options="dropzoneLogo"
                    >
                    </vue-dropzone>
                  </div>
                  <div class="col-md-12">
                    <div class="row" v-if="uploadLogoAllowed">
                      <div class="col-md-12 mt-3">
                        <button class="btn btn-default" @click.prevent="uploadNewLogo">
                          {{ __('Upload new logo') }}
                        </button>
                      </div>
                    </div>
                    <div v-for="(logo, idx) in teamLogos" class="row image-row">
                      <div class="col-md-12 survey-settings-image" @click.prevent="deleteImage(logo, idx)">
                        <img class="img-fluid" style="max-height: 150px" :src="getSurveyImageUrl(logo)" />
                        <div class="image-delete">
                          <i class="fas fa-trash"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <label for="surveysImage">{{ __('Surveys Photo') }}</label>
                <small
                  class="form-text"
                  v-bind:class="{ 'text-danger': teamMainImages.length == teamSurveyImagesLimit }"
                  >{{ teamMainImages.length }} out of {{ teamSurveyImagesLimit }} uploaded</small
                >
                <small id="surveysImageHelp" class="form-text text-muted">{{
                  __('This is the main image that will be on the right of your surveys')
                }}</small>
                <div class="row mt-3">
                  <div class="col-md-12" v-if="uploadImageAllowed">
                    <vue-dropzone
                      v-on:vdropzone-complete="imageUploaded"
                      v-on:vdropzone-success="imageUploadedSuccess"
                      v-on:vdropzone-error="uploadError"
                      v-if="mounted"
                      id="dropzone-survey-image"
                      :options="dropzoneSurveyPhoto"
                    >
                    </vue-dropzone>
                  </div>
                  <div class="col-md-12">
                    <div class="row" v-if="uploadImageAllowed">
                      <div class="col-md-12 mt-3">
                        <button class="btn btn-default" @click.prevent="uploadNewImage">
                          {{ __('Upload new image') }}
                        </button>
                      </div>
                    </div>
                    <div v-for="(mainImage, idx) in teamMainImages" class="row image-row">
                      <div class="col-md-12 survey-settings-image" @click.prevent="deleteImage(mainImage, idx)">
                        <img class="img-fluid" style="max-height: 150px" :src="getSurveyImageUrl(mainImage)" />
                        <div class="image-delete">
                          <i class="fas fa-trash"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div class="card" v-if="openTab === 'reporting'">
      <div class="card-body">
        <div class="row">
          <form class="col-md-6" id="alerts-form" @submit.prevent="">
            <h5 class="header-title mb-3">{{ __('Set up Alerts') }}</h5>
            <p class="readable-text">
              {{ __('All the emails included in here will be notified whenever a survey is filled.') }}
            </p>
            <ul class="list-group mb-3">
              <li
                class="list-group-item d-flex justify-content-between align-items-center"
                :key="emailObject.email"
                v-for="emailObject in sortedAlertEmails"
              >
                <span>
                  <span v-if="emailObject.type === 'all'">
                    {{ __('All Alerts') }}
                  </span>
                  <span v-else-if="emailObject.type === 'negative'">
                    {{ __('Only Negative') }}
                  </span>
                  <br />
                  <span v-if="emailObject.surveys && emailObject.surveys.length > 0">Selected Surveys</span>
                  <button
                    class="btn btn-link btn-sm"
                    @click="toggleSurveyList(emailObject)"
                    v-if="emailObject.surveys && emailObject.surveys.length > 0"
                  >
                    <i :class="emailObject.toggled ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                  </button>
                  <ul class="custom-list list-unstyled ml-3" v-if="emailObject.toggled">
                    <li v-for="survey in emailObject.surveys">
                      <i class="fas fa-check-circle mr-2 text-success"></i>
                      {{ survey.title }}
                    </li>
                  </ul>
                  <div>
                    Email: <a class="blue-link" :href="`mailto:${emailObject.email}`">{{ emailObject.email }}</a>
                  </div>
                </span>

                <button
                  class="btn btn-default btn-fixed-width"
                  type="button"
                  @click.prevent="removeAlertEmail(emailObject.id)"
                >
                  <span class="text-danger">{{ __('Delete') }}</span>
                </button>
              </li>
            </ul>

            <div>
              <div class="input-group mb-2">
                <select v-model="alertType" id="alertType">
                  <option value="all">{{ __('All Alerts') }}</option>
                  <option value="negative">{{ __('Only negative') }}</option>
                </select>
                <input
                  type="text"
                  class="form-control"
                  v-model="alertEmail"
                  placeholder="Enter email to send alerts"
                  aria-label="Enter email to send alerts"
                  aria-describedby="alert-email-text"
                />
                <div v-if="!isValidEmail && alertEmail.length > 0" class="error">
                  Please enter a valid email address.
                </div>
              </div>
              <div class="row justify-content-md-center">
                <div class="col-md-12">
                  <p class="readable-text">
                    {{ __('Select the surveys you want to be notified about') }}
                  </p>
                </div>
                <div class="col-md-12 ml-3" v-for="survey in surveys" :key="survey.id">
                  <div class="custom-control custom-switch">
                    <input
                      type="checkbox"
                      class="custom-control-input"
                      :id="`survey-${survey.id}`"
                      :value="survey.id"
                      @change="toggleSelectedSurvey(survey.id)"
                      :disabled="alertEmail.length == 0"
                      :checked="isSelected(survey.id)"
                    />
                    <label class="custom-control-label" :for="`survey-${survey.id}`">{{ survey.title }}</label>
                  </div>
                </div>
              </div>

              <div class="text-right">
                <button class="btn btn-success" @click.prevent="addAlertEmail(alertEmail)">{{ __('Add') }} +</button>
              </div>
            </div>
          </form>
          <form class="col-md-6 edit-topics">
            <h5 class="header-title mb-3">{{ __('Survey Topics') }}</h5>
            <p class="readable-text">
              {{ __('Topics can be assigned to survey questions, so that you can connect questions with categories.') }}
            </p>
            <ul class="list-group">
              <li v-for="(topic, idx) in topics" :key="topic.id" class="list-group-item d-flex justify-content-between">
                <span v-show="topic.editing != true">{{ topic.name }}</span>
                <input class="form-control" type="text" v-show="topic.editing == true" v-model="topic.name" />
                <div v-show="topic.editing != true">
                  <button
                    class="btn btn-warning btn-gradient-warning"
                    v-on:click.prevent="setTopicEditingFlag(idx, true)"
                  >
                    {{ __('Edit') }}
                  </button>
                  <button class="btn btn-danger btn-gradient-danger" v-on:click.prevent="deleteTopic(idx, topic.id)">
                    {{ __('Delete') }}
                  </button>
                </div>
                <div v-show="topic.editing == true">
                  <button
                    v-if="topic.id"
                    class="btn btn-primary save-button"
                    v-on:click.prevent="updateTopic(idx, topic)"
                  >
                    {{ __('Save') }}
                  </button>
                  <button
                    v-if="!topic.id"
                    class="btn btn-success create-button btn-gradient-success"
                    v-on:click.prevent="saveTopic(idx, topic)"
                  >
                    {{ __('Create') }}
                  </button>
                </div>
              </li>
              <li class="list-group-item">
                <button class="btn btn-primary pull-right" v-on:click.prevent="addTopic">
                  {{ __('Add new topic') }}
                </button>
              </li>
            </ul>
          </form>
        </div>
      </div>
    </div>
    <SurveyPmsSettings v-if="openTab === 'pms'" />
  </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone';
import SurveysService from '../../services/surveys-service';
import SurveyPmsSettings from './SurveysPmsSettings.vue';

export default {
  components: {
    vueDropzone: vue2Dropzone,
    SurveyPmsSettings,
  },
  data() {
    return {
      settings: {
        alertEmails: [],
      },
      alertType: '',
      selectedSurveys: [],
      surveys: [],
      openTab: 'address',
      topics: [],
      settings: {},
      generalInfo: {
        country: '',
        city: '',
        address: '',
        phone: '',
        email: '',
      },
      alertEmail: '',
      alertType: 'all',
      teamLogos: [],
      teamMainImages: [],
      teamSurveyImagesLimit: 0,
      uploadLogoAllowed: false,
      uploadImageAllowed: false,
      lastTimeLogoUpdated: new Date().getTime(),
      lastTimeImageUpdated: new Date().getTime(),
      dropzoneOptions: {
        acceptedFiles: 'image/*',
        addRemoveLinks: true,
        maxFilesize: 2,
        maxFiles: 1,
        headers: { Authorization: Spark.jwtToken },
      },
      dropzoneLogo: null,
      dropzoneSurveyPhoto: null,
      mounted: false,
    };
  },
  watch: {
  alertEmail(newVal) {
    if (newVal !== newVal.trim()) {
      this.alertEmail = newVal.trim();
      }
    }
  },
  computed: {
    generalSettingsFilled() {
      return (
        this.generalInfo.phone.trim() != '' &&
        this.generalInfo.email.trim() != '' &&
        this.generalInfo.address.trim() != '' &&
        this.generalInfo.country.trim() != '' &&
        this.generalInfo.city.trim() != ''
      );
    },
    isValidEmail() {
    // Simple regex for email validation
    const emailRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return emailRegex.test(this.alertEmail.trim());
    },
    sortedAlertEmails() {
    if (!this.settings.alertEmails) {
      return [];
    }
    return this.settings.alertEmails.slice().sort((a, b) => {
      const order = ['all', 'negative'];
      return order.indexOf(a.type) - order.indexOf(b.type);
    });
  },
  },
  methods: {
    setTopicEditingFlag(idx, flag) {
      Vue.set(this.topics[idx], 'editing', flag);
    },
    toggleSurveyList(emailObject) {
      if (emailObject.toggled === undefined) {
        this.$set(emailObject, 'toggled', false);
      }
      emailObject.toggled = !emailObject.toggled;
    },
    isSelected(surveyId) {
      return this.selectedSurveys.includes(surveyId);
    },
    toggleSelectedSurvey(surveyId) {
      const index = this.selectedSurveys.indexOf(surveyId);
      if (index !== -1) {
        this.selectedSurveys.splice(index, 1);
      } else {
        this.selectedSurveys.push(surveyId);
      }
    },
    getSurveyNames(selectedSurveyIds) {
      if (!selectedSurveyIds || selectedSurveyIds.length === 0) {
        return 'No surveys selected';
      }
      const selectedSurveys = this.surveys.filter((survey) => selectedSurveyIds.includes(survey.id));
      console.log('Selected surveys', selectedSurveys);
      return selectedSurveys.map((survey) => survey.title).join(', ');
    },
    async getSurveys() {
      const results = await SurveysService.index();
      this.surveys = results.data.map((survey) => ({
        id: survey.id,
        title: survey.title,
      }));
    },
    async updateSettings(settings) {
      const data = await SurveysService.settings.update(settings);

      if (data.error) {
        swal('Settings not updated', data.error, 'error');
      } else {
        swal('Settings updated', data.message, 'success');
      }
    },
    async addTopic() {
      this.topics.push({
        name: 'New Topic',
        editing: true,
      });
    },
    async saveTopic(idx, topic) {
      this.topics[idx].lock = true;

      const data = await SurveysService.topics.create(topic);
      if (data.error) {
        swal('Topic not created', data.error, 'error');
      } else {
        this.topics[idx].id = data.id;
        this.topics[idx].lock = false;
        this.topics[idx].editing = false;
      }
    },
    async updateTopic(idx, topic) {
      const topicClone = Object.assign({}, topic);
      delete topicClone.id;
      delete topicClone.editing;
      delete topicClone.lock;

      this.topics[idx].lock = true;
      const data = await SurveysService.topics.update(topic.id, topicClone);

      if (data.error) {
        swal('Topic not updated', data.error, 'error');
      } else {
        this.topics[idx].editing = false;
        this.topics[idx].lock = false;
      }
    },
    async deleteTopic(idx, id) {
      swal(
        {
          title: 'Are you sure you want to delete this topic?',
          text: 'If you delete this topic, you will lose any association between the survey responses and the topic',
          type: 'warning',
          showCancelButton: true,
          confirmButtonClass: 'btn btn-danger',
          confirmButtonText: 'Delete it',
          closeOnConfirm: false,
        },
        async () => {
          const response = await SurveysService.topics.delete(id);
          swal('Deleted', response.message, 'success');
          this.topics.splice(idx, 1);
        },
      );
    },
    async addAlertEmail() {
      let payload = {
        email: this.alertEmail,
        type: this.alertType,
        selectedSurveys: this.selectedSurveys,
      };

      const response = await SurveysService.settings.addAlertEmail(payload);

      if (response.error) {
        swal('Failed', response.error, 'error');
      } else {
        this.alertEmail = '';
        swal('Congrats', response.data.message, 'success');
        const settings = await SurveysService.settings.index();
        this.settings = settings;
        this.selectedSurveys = [];
      }
    },
    async removeAlertEmail(id) {
      const response = await SurveysService.settings.removeAlertEmail(id);

      if (response.error) {
        swal('Failed', response.error, 'error');
      } else {
        swal('Congrats', response.data.message, 'success');
        const settings = await SurveysService.settings.index();
        this.settings = settings;
      }
    },
    uploadNewLogo() {
      this.uploadLogoAllowed = true;
      Vue.nextTick(() => {
        document.getElementById('dropzone-logo').click();
      });
    },
    logoUploaded() {
      this.lastTimeLogoUpdated = new Date().getTime();
      setTimeout(() => {
        this.uploadLogoAllowed = false;
        setTimeout(() => {
          this.checkImageLimits();
        }, 500);
      }, 2500);
    },
    getSurveyImageUrl(image) {
      let t = image.filename.split(/[- .]/)[1];

      return process.env.MIX_API_URL + '/private/' + image.type + '/' + Spark.state.currentTeam.id + '?t=' + t;
    },
    getTeamLogosUrl() {
      return process.env.MIX_API_URL + '/private/team-logos/' + Spark.state.currentTeam.id;
    },
    uploadError(file, error) {
      swal('Image upload error', error, 'error');
    },
    uploadNewImage() {
      this.uploadImageAllowed = true;
      Vue.nextTick(() => {
        document.getElementById('dropzone-survey-image').click();
      });
    },
    logoUploadedSuccess(file) {
      this.teamLogos.push(JSON.parse(file.xhr.response));
    },
    imageUploadedSuccess(file) {
      this.teamMainImages.push(JSON.parse(file.xhr.response));
    },
    imageUploaded() {
      this.lastTimeImageUpdated = new Date().getTime();
      setTimeout(() => {
        this.uploadImageAllowed = false;
        setTimeout(() => {
          this.checkImageLimits();
        }, 500);
      }, 2500);
    },
    getTeamImagesUrl() {
      return process.env.MIX_API_URL + '/private/team-main-images/' + Spark.state.currentTeam.id;
    },
    deleteImage(image, idx) {
      swal(
        {
          title: 'Are you sure you want to delete this ' + image.type + ' image?',
          text: '',
          type: 'warning',
          showCancelButton: true,
          confirmButtonClass: 'btn btn-danger',
          confirmButtonText: 'Delete it',
          closeOnConfirm: false,
        },
        async () => {
          axios.delete(process.env.MIX_API_URL + '/private/' + image.type + '/' + image.id).then((response) => {
            swal('Deleted', response.message, 'success');
            if (image.type == 'main') {
              this.teamMainImages.splice(idx, 1);
            } else if (image.type == 'logo') {
              this.teamLogos.splice(idx, 1);
            }
            this.checkImageLimits();
          });
        },
      );
    },
    checkImageLimits() {
      this.uploadLogoAllowed = this.teamLogos.length < this.teamSurveyImagesLimit;
      this.uploadImageAllowed = this.teamMainImages.length < this.teamSurveyImagesLimit;
    },
  },
  async mounted() {
    this.dropzoneLogo = Object.assign({}, this.dropzoneOptions, {
      dictDefaultMessage: "Click here to upload logo your hotel's logo",
      url: process.env.MIX_API_URL + '/private/upload/file/logo',
    });

    this.dropzoneSurveyPhoto = Object.assign({}, this.dropzoneOptions, {
      dictDefaultMessage: 'Click here to upload an image to show on the right side of the survey',
      url: process.env.MIX_API_URL + '/private/upload/file/main-image',
    });

    axios.get(this.getTeamLogosUrl()).then((response) => {
      this.teamLogos = response.data;
    });

    axios.get(this.getTeamImagesUrl()).then((response) => {
      this.teamMainImages = response.data;
    });

    this.teamSurveyImagesLimit = await SurveysService.getSurveyImagesLimit();
    this.checkImageLimits();

    const topics = await SurveysService.topics.index();
    this.topics = topics.data;

    const settings = await SurveysService.settings.index();

    settings.alertEmails.forEach((emailObject) => {
      emailObject.toggled = false;
    });
    this.settings = settings;
    // When general settings are stored, pre-fill the inputs
    if (this.settings.address) {
      this.generalInfo.country = settings.country;
      this.generalInfo.city = settings.city;
      this.generalInfo.address = settings.address;
      this.generalInfo.phone = settings.phone;
      this.generalInfo.email = settings.email;
      this.generalInfo.strictConsent = settings.strictConsent;
    }
    this.getSurveys();

    this.mounted = true;
  },
};
</script>
<style>
a.blue-link {
  font-size: 13px;
}
</style>
